import React from 'react';
import { UserRole } from '../../../apiClients/workspace/types';
import ParticipantAvatar from '../../../components/shared/ParticipantAvatar';
import { useLanguage } from '../../../hooks/shared';
import { getParticipantStatusIcon } from '../../../lib/participantUtils';
import { getLevelIcon } from '../../../lib/userUtils';
import { cn } from '../../../lib/utils';
import { useUserStore } from '../../../stores';
import { Training } from '../types/calendarTypes';
import {
  formatHHmm,
  getEventStatusByParticipantsView,
  getEventStatusView,
} from '../utils/trainingUtils';
import AdminActions from './AdminActions';
import ParticipantActions from './ParticipantActions';

interface TimeSlotProps {
  training: Training;
  onClick?: (e?: React.MouseEvent) => void;
  className?: string;
  compact?: boolean; // Simplified mode without participant list
  showLevelText?: boolean; // Whether to show level text (default true)
  hideTime?: boolean; // Hide time (when shown in header)
  showTemplateDescription?: boolean; // Whether to show template description (default false)
}

const TimeSlotComponent: React.FC<TimeSlotProps> = ({
  training,
  onClick,
  compact = false,
  showLevelText = true,
  hideTime = false,
  showTemplateDescription = false,
  className,
}) => {
  const { t } = useLanguage();
  const userStore = useUserStore();

  const isTemplate = training.isTemplate;
  const hasParticipants = (training.participants?.length || 0) > 0;

  // Existing status computations (kept)
  const isPastStatus =
    training.status === 'Finished' || training.status === 'Cancelled';
  const cardStatus = React.useMemo(() => {
    return training.status === 'Active'
      ? getEventStatusByParticipantsView(training.participants, t)
      : getEventStatusView(training.status, t);
    return getEventStatusView(training.status, t);
  }, [training.status, training.participants, t]);

  // Special styling for templates
  const templateStyles = isTemplate
    ? {
        bg: 'bg-gray-100 border-gray-300',
        opacity: 'opacity-60',
        cursor: 'cursor-pointer', // Make templates clickable
      }
    : {};

  return (
    <div
      className={cn(
        'rounded-lg border p-3 transition-shadow relative',
        isTemplate ? templateStyles.bg : cardStatus.bg,
        isTemplate ? templateStyles.opacity : '',
        isTemplate ? templateStyles.cursor : 'cursor-pointer hover:shadow-md',
        className
      )}
      onClick={onClick}
    >
      {/* Time, level and card status */}
      <div className="flex justify-between items-start mb-3 gap-3">
        <div className="flex flex-col gap-1 min-w-0 flex-1">
          {!hideTime && (
            <div
              className={cn(
                'font-semibold',
                isPastStatus ? 'text-gray-600' : 'text-gray-800'
              )}
            >
              {formatHHmm(training.startTime)} - {formatHHmm(training.endTime)}
            </div>
          )}
          <div
            className={cn(
              'text-sm flex items-center gap-1',
              isPastStatus ? 'text-gray-500' : 'text-gray-600'
            )}
          >
            {isTemplate ? '📋' : hasParticipants ? cardStatus.icon : null}
            <span className="text-sm font-normal break-words">
              {isTemplate
                ? t('calendar.scheduled')
                : hasParticipants
                  ? cardStatus.text
                  : t('calendar.noParticipants')}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {hasParticipants && (
            <span
              className={cn(
                'px-2 py-1 rounded-full text-xs font-medium border',
                isPastStatus
                  ? 'border-gray-300 bg-gray-100 text-gray-600'
                  : 'border-gray-300 bg-gray-50 text-gray-700'
              )}
              title={training.levelName}
            >
              {getLevelIcon(training.level, (training as any).levelIconId)}
              {showLevelText && training.levelName && ` ${training.levelName}`}
            </span>
          )}
          {!isTemplate &&
            training.currentUserRoles?.includes(UserRole.Admin) && (
              <AdminActions training={training} />
            )}
        </div>
      </div>

      {/* Participant list */}
      {!compact &&
        training.participants &&
        training.participants.length > 0 && (
          <div className="space-y-2 mt-2">
            {training.participants.map((participant) => {
              // Check if this participant replaces someone or is replaced
              const replacedParticipant = participant.replacementFor
                ? userStore.users.get(participant.replacementFor)
                : null;
              const isReplacing = !!participant.replacementFor;
              const isReplacedStatus = participant.status === 'Replaced';

              return (
                <div
                  key={participant.id}
                  className={cn(
                    'flex items-center gap-3 p-1 text-sm rounded-lg',
                    isPastStatus ? 'text-gray-600' : 'text-gray-600',
                    participant.id === training.currentUserId
                      ? 'bg-blue-50 border border-blue-200'
                      : 'hover:bg-gray-50'
                  )}
                >
                  <ParticipantAvatar
                    name={participant.name}
                    photoUrl={participant.photoUrl}
                  />
                  <span
                    className={cn(
                      'flex-1',
                      participant.id === training.currentUserId &&
                        'font-medium text-blue-700'
                    )}
                  >
                    <span
                      className={cn(
                        isReplacing && 'text-green-600 font-medium',
                        isReplacedStatus && 'line-through text-gray-500'
                      )}
                    >
                      {participant.name}
                    </span>
                    {(participant.status === 'Cancelled' ||
                      participant.status === 'Replaced') &&
                      participant.cancelledWithValidExcuse && (
                        <span
                          className="text-xs"
                          title={t('calendar.validExcuse')}
                        >
                          🍀
                        </span>
                      )}
                    {participant.id === training.currentUserId && (
                      <span className="ml-2 text-xs text-blue-500">
                        ({t('calendar.you')})
                      </span>
                    )}

                    {isReplacing && replacedParticipant && (
                      <>
                        <span className="mx-1 text-gray-400">→</span>

                        <span className="text-gray-500">
                          {replacedParticipant.name}

                          {replacedParticipant.id ===
                            training.currentUserId && (
                            <span className="ml-2 text-xs text-blue-500">
                              ({t('calendar.you')})
                            </span>
                          )}
                        </span>
                      </>
                    )}
                  </span>
                  {!isTemplate && (
                    <span
                      className="text-md"
                      title={`${t('common.status')}: ${participant.status}`}
                    >
                      {getParticipantStatusIcon(participant.status)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

      {/* Template description */}
      {isTemplate && showTemplateDescription && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 text-blue-700">
            <span className="text-lg">⏰</span>
            <span className="text-sm font-medium">
              {t('calendar.templateDescription')}
            </span>
          </div>
          <p className="text-sm text-blue-600 mt-1">
            {t('calendar.templateDescriptionText')}
          </p>
        </div>
      )}

      {/* Compact mode - just show participant count */}
      {compact && (
        <div className="text-sm text-gray-600 mt-2">
          <span>
            {t('calendar.participantsCount', {
              count: training.participants.length,
            })}
          </span>
        </div>
      )}

      {/* Action buttons */}
      {!isTemplate && (
        // User can be an admin and a participant at the same time
        // Now it's onlly for me, but make more user-friendly UI for future cases
        // TODO Refactor
        <>
          {training.currentUserRoles?.includes(UserRole.Participant) && (
            <ParticipantActions training={training} />
          )}
        </>
      )}
    </div>
  );
};

export default React.memo(TimeSlotComponent);
